import React, { createContext, useState, useContext, useCallback, useMemo, useEffect, useId, useRef } from 'react';
import PropTypes from 'prop-types';

const ModalBackHandlerContext = createContext(null);

export const useModalBackHandler = () => {
  const context = useContext(ModalBackHandlerContext);
  if (!context) {
    throw new Error('useModalBackHandler must be used within a ModalBackHandlerProvider');
  }
  return context;
};

/**
 * A hook to declaratively register a modal's close handler for the back button.
 * @param {function} handler - The function to call on back press (typically the modal's onClose).
 * @param {boolean} enabled - Whether this handler is currently active (i.e., if the modal is open).
 */
export const useRegisterModalBackHandler = (handler, enabled) => {
  const { registerModal, unregisterModal } = useModalBackHandler();
  const id = useId();
  const handlerRef = useRef(handler);

  useEffect(() => {
    handlerRef.current = handler;
  }, [handler]);

  const stableHandler = useCallback(() => {
    if (handlerRef.current) {
      handlerRef.current();
    }
  }, []);

  useEffect(() => {
    if (enabled) {
      registerModal(id, stableHandler);
      return () => {
        unregisterModal(id);
      };
    }
  }, [id, stableHandler, enabled, registerModal, unregisterModal]);
};

export const ModalBackHandlerProvider = ({ children }) => {
  const [modals, setModals] = useState([]);
  const modalsRef = useRef(modals);

  useEffect(() => {
    modalsRef.current = modals;
  }, [modals]);

  const registerModal = useCallback((id, closeFunction) => {
    setModals(prev => {
      if (prev.some(m => m.id === id)) {
        return prev;
      }
      return [...prev, { id, closeFunction }];
    });
  }, []);

  const unregisterModal = useCallback((id) => {
    setModals(prev => prev.filter(m => m.id !== id));
  }, []);

  const handleBackPress = useCallback(() => {
    // Use a timeout to ensure this runs after the current event loop,
    // preventing potential race conditions with state updates.
    setTimeout(() => {
      const currentModals = modalsRef.current;
      if (currentModals.length > 0) {
        const topModal = currentModals[currentModals.length - 1];
        console.log(`[ModalBackHandler] Closing top modal: ${topModal.id}`);
        topModal.closeFunction();
      } else {
        console.log('[ModalBackHandler] Back press ignored, no modals open.');
      }
    }, 0);
  }, []);

  const value = useMemo(() => ({
    registerModal,
    unregisterModal,
    handleBackPress,
    modalCount: modals.length,
  }), [registerModal, unregisterModal, handleBackPress, modals.length]);

  return (
    <ModalBackHandlerContext.Provider value={value}>
      {children}
    </ModalBackHandlerContext.Provider>
  );
};

ModalBackHandlerProvider.propTypes = {
  children: PropTypes.node.isRequired,
};